// AdMob configuration + lifecycle helpers.
//
// Ads are NATIVE-ONLY (Capacitor AdMob). The website (dailyhisaab.com) never
// shows ads. Free users see a bottom banner; active subscribers (and users who
// redeemed reward points for temporary Premium time) are ad-free — see
// `useShowAds`.
//
// ─────────────────────────────────────────────────────────────────────────────
// ⚠️ BEFORE THE PRODUCTION RELEASE: set USE_TEST_ADS = false (one line below).
// The real App ID + banner unit are already wired (here + strings.xml). While
// USE_TEST_ADS is true the SDK serves Google TEST ads even with the real unit id
// — safe to tap during QA. Shipping with test ads on violates AdMob policy.
// ─────────────────────────────────────────────────────────────────────────────

import { Capacitor } from "@capacitor/core";

// Real AdMob ad units (DailyHisaab Android).
export const AD_UNIT_BANNER = "ca-app-pub-3836406786816668/6696011689";
export const AD_UNIT_INTERSTITIAL = "ca-app-pub-3836406786816668/1517008054";

// Free-tier ad strategy: a full-screen interstitial at a natural break (AFTER a
// sale completes), with no persistent banner clutter. Flip BANNER_ENABLED back
// on if you ever want the banner too.
export const BANNER_ENABLED = false;
export const INTERSTITIAL_ENABLED = true;
// TEMP: app-open disabled pending native crash diagnosis. With this false,
// initAppOpen() never calls the native plugin, so it stays fully dormant.
export const APP_OPEN_ENABLED = false;

// App-open ads use a custom native plugin (the AdMob plugin lacks the format).
// Google's TEST app-open unit is used until a real one is created in AdMob.
const TEST_APP_OPEN_AD_UNIT = "ca-app-pub-3940256099942544/9257395921";
export const AD_UNIT_APP_OPEN = TEST_APP_OPEN_AD_UNIT; // TODO: real app-open unit id

// While true, isTesting forces Google TEST ads (safe to tap). Flip to false for
// the production release build.
export const USE_TEST_ADS = true; // TODO: false before production release

// Reserved layout height (px) for the anchored adaptive bottom banner. Keep in
// sync with the ads padding class in App.tsx.
export const AD_BANNER_HEIGHT_PX = 60;

let initStarted = false;

/**
 * Initialize the AdMob SDK and run the UMP (GDPR) consent flow once.
 * Native-only; a safe no-op on web. Never throws — ad failures must not break
 * the app.
 */
export async function initAds(): Promise<void> {
  if (initStarted || !Capacitor.isNativePlatform()) return;
  initStarted = true;
  try {
    const { AdMob, AdmobConsentStatus } = await import("@capacitor-community/admob");
    await AdMob.initialize({ initializeForTesting: USE_TEST_ADS });

    // UMP consent — best-effort. If the AdMob account has no consent form
    // configured ("Publisher misconfiguration"), or in non-EEA regions, this is
    // a no-op; it must never block ad initialization.
    try {
      const consent = await AdMob.requestConsentInfo();
      if (consent.isConsentFormAvailable && consent.status === AdmobConsentStatus.REQUIRED) {
        await AdMob.showConsentForm();
      }
    } catch (consentErr) {
      console.warn("[ads] consent skipped", consentErr);
    }

    // Warm up the first interstitial so it's ready at the next natural break.
    void preloadInterstitial();
  } catch (err) {
    initStarted = false; // allow a later retry
    console.warn("[ads] initialization failed", err);
  }
}

// ── Interstitial (shown AFTER a sale completes) ──────────────────────────────
// Full-screen ad at a natural break. NEVER shown before/during a sale. Guarded
// by a frequency cap + a skip-first-sale rule so a busy shop isn't spammed, and
// only shown if one is already preloaded (the user never waits on a load).

const INTERSTITIAL_MIN_GAP_MS = 3 * 60 * 1000; // ≥ 3 min between interstitials
const INTERSTITIAL_SKIP_FIRST = 1;             // skip the first sale of a session

let interstitialReady = false;
let interstitialLoading = false;
let lastInterstitialAt = 0;
let saleCloseCount = 0;

/** Preload an interstitial so it can show instantly. Native-only; never throws. */
export async function preloadInterstitial(): Promise<void> {
  if (!INTERSTITIAL_ENABLED || interstitialReady || interstitialLoading) return;
  if (!Capacitor.isNativePlatform()) return;
  interstitialLoading = true;
  try {
    const { AdMob } = await import("@capacitor-community/admob");
    await AdMob.prepareInterstitial({ adId: AD_UNIT_INTERSTITIAL, isTesting: USE_TEST_ADS });
    interstitialReady = true;
  } catch (err) {
    console.warn("[ads] interstitial preload failed", err);
  } finally {
    interstitialLoading = false;
  }
}

/**
 * Show an interstitial at a natural break (call when a sale's receipt closes).
 * No-op unless: native, interstitials enabled, past the skip-first-sale rule,
 * outside the min-gap window, and an ad is already preloaded. Re-preloads after.
 * Never throws.
 */
export async function maybeShowInterstitial(): Promise<void> {
  if (!INTERSTITIAL_ENABLED || !Capacitor.isNativePlatform()) return;

  saleCloseCount += 1;
  if (saleCloseCount <= INTERSTITIAL_SKIP_FIRST) {
    void preloadInterstitial(); // warm up for the next eligible break
    return;
  }

  const now = Date.now();
  if (now - lastInterstitialAt < INTERSTITIAL_MIN_GAP_MS) return;
  if (!interstitialReady) {
    void preloadInterstitial(); // not ready this time; ready for next
    return;
  }

  try {
    const { AdMob } = await import("@capacitor-community/admob");
    interstitialReady = false;
    lastInterstitialAt = now;
    await AdMob.showInterstitial();
  } catch (err) {
    console.warn("[ads] interstitial show failed", err);
  } finally {
    void preloadInterstitial(); // load the next one
  }
}

/**
 * Force an interstitial NOW, bypassing the frequency cap and skip-first rule —
 * used for the "ad after every receipt print" trigger. Still native-only and
 * still only shows if one is already preloaded (never makes the user wait on a
 * load); if none is ready it preloads for the next print. Sets the cap timestamp
 * so a following capped call (e.g. the receipt-close handler) won't double-fire.
 * Never throws.
 */
export async function showInterstitialNow(): Promise<void> {
  if (!INTERSTITIAL_ENABLED || !Capacitor.isNativePlatform()) return;
  if (!interstitialReady) {
    void preloadInterstitial(); // not ready now; warm up for the next print
    return;
  }
  try {
    const { AdMob } = await import("@capacitor-community/admob");
    interstitialReady = false;
    lastInterstitialAt = Date.now();
    await AdMob.showInterstitial();
  } catch (err) {
    console.warn("[ads] forced interstitial show failed", err);
  } finally {
    void preloadInterstitial(); // load the next one
  }
}

// ── Ad suppression ───────────────────────────────────────────────────────────
// The banner is hidden while a billing/checkout surface is active (cart open,
// receipt/success popup) so it never overlaps the Sell button — overlapping
// money buttons risks accidental taps and AdMob invalid-traffic flags.
// Reference-counted so multiple concurrent surfaces compose correctly.

let suppressionCount = 0;
const suppressionListeners = new Set<() => void>();

/** Suppress the banner; returns a release fn. Pairs with `useSuppressAdsWhile`. */
export function pushAdSuppression(): () => void {
  suppressionCount += 1;
  suppressionListeners.forEach((cb) => cb());
  let released = false;
  return () => {
    if (released) return;
    released = true;
    suppressionCount = Math.max(0, suppressionCount - 1);
    suppressionListeners.forEach((cb) => cb());
  };
}

export function areAdsSuppressed(): boolean {
  return suppressionCount > 0;
}

export function subscribeAdSuppression(cb: () => void): () => void {
  suppressionListeners.add(cb);
  return () => {
    suppressionListeners.delete(cb);
  };
}

// ── App Open ads (custom native plugin) ──────────────────────────────────────
// Shown when the app returns to the foreground (skips cold-start launch, capped
// to once / 4h natively). Native-only; safe no-ops on web.

/** Initialize app-open ads. Call when a free user is active. Never throws. */
export async function initAppOpen(): Promise<void> {
  if (!APP_OPEN_ENABLED || !Capacitor.isNativePlatform()) return;
  try {
    const { AppOpenAdManager } = await import("./appOpenAd");
    await AppOpenAdManager.initialize({ adId: USE_TEST_ADS ? TEST_APP_OPEN_AD_UNIT : AD_UNIT_APP_OPEN });
  } catch (err) {
    console.warn("[ads] app-open init failed", err);
  }
}

/** Enable/disable app-open ads (disable when the user becomes a paid subscriber). */
export async function setAppOpenEnabled(enabled: boolean): Promise<void> {
  if (!APP_OPEN_ENABLED || !Capacitor.isNativePlatform()) return;
  try {
    const { AppOpenAdManager } = await import("./appOpenAd");
    await AppOpenAdManager.setEnabled({ enabled });
  } catch {
    /* noop */
  }
}
